﻿import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DatePicker, Modal, Spin, Tooltip } from 'antd'
import type { AxiosResponse } from 'axios'
import dayjs from 'dayjs'
import { useSearchParams } from 'react-router-dom'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { useBackToParent, usePermission, usePersistentPageSize } from '@/hooks'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { isHandledAuthError } from '@/services/client'
import { paymentJobsApi } from '@/services/paymentJobs/api'
import type { BankInfoIssue, PaymentJobResponse } from '@/services/paymentJobs/types'
import { useToast } from '@/widgets/common'
import ConfirmDialog from '@/widgets/common/ConfirmDialog'
import BankInfoIssueDialog from '@/widgets/payment/BankInfoIssueDialog'
import { downloadBlob, extractBlobErrorMessage } from '@/utils/download'
import useSearchPersistence from '@/hooks/useSearchPersistence'
import { useColumnVisibility } from '@/hooks/useColumnVisibility'
import { BackButton, DemoBadge, DemoInput, DemoSelect, Pagination, ResultCard, ResizeHandle, SearchCard, SortableHeader, TableLoadingRow } from '@/widgets/demo/ListPrimitives';
import { ColumnVisibilityControl } from '@/widgets/demo/ColumnVisibilityControl';
import { demoButtonClass, computePagination, useResizableColumns, useSortState, type ColumnWidthMap, type SortState } from '@/widgets/demo/listHelpers';
const PAYMENT_JOB_LIST_COLUMN_DEFAULTS: ColumnWidthMap = {
  select: 48,
  jobNo: 128,
  jobName: 208,
  transferDate: 120,
  recipientCount: 120,
  status: 152,
  registrant: 96,
  registeredAt: 120,
  updater: 96,
  updatedAtStr: 120,
  action: 240,
}

const PAYMENT_JOB_LIST_COLUMN_ORDER = [
  'select',
  'jobNo',
  'jobName',
  'transferDate',
  'recipientCount',
  'status',
  'registrant',
  'registeredAt',
  'updater',
  'updatedAtStr',
  'action',
] as const

// 列表示設定（No.30 横展開）用の画面キーと列ラベル。ヘッダ表示テキストと揃える。
const PAYMENT_JOB_LIST_COLUMN_VISIBILITY_KEY = 'payment-job-list'

// 選択チェックボックス列(select)・主キー相当のリスト番号(jobNo)・操作列(action)は常に表示。
const PAYMENT_JOB_LIST_ALWAYS_VISIBLE_COLUMNS = ['select', 'jobNo', 'action']

const PAYMENT_JOB_LIST_COLUMN_LABELS: Record<(typeof PAYMENT_JOB_LIST_COLUMN_ORDER)[number], string> = {
  select: '選択',
  jobNo: 'リスト番号',
  jobName: 'リスト名',
  transferDate: '振込指定日',
  recipientCount: '対象世帯数',
  status: 'ステータス',
  registrant: '作成者',
  registeredAt: '作成時間',
  updater: '更新者',
  updatedAtStr: '更新時間',
  action: '操作',
}

const PAYMENT_JOB_LIST_COLUMN_VISIBILITY_ITEMS = PAYMENT_JOB_LIST_COLUMN_ORDER.map((id) => ({
  id,
  label: PAYMENT_JOB_LIST_COLUMN_LABELS[id],
}))

type SearchValues = {
  jobNo: string
  jobName: string
  status: string
  registeredBy: string
  includeDeleted: boolean
  transferDateFrom: string | null
  transferDateTo: string | null
  excludeCompleted: boolean
}

const EXCLUDE_COMPLETED_STATUSES = 'ZENGIN_PROD_EXPORTED,COMPLETED_CASH'

/**
 * 画面で一度に選択できる世帯数の上限（業務確定値: 1000世帯まで許可・1001世帯以上は拒否）。
 * 選択リストの「対象世帯数」(recipientCount) の合計で判定する。一括出力（支給者CSV/通知書/振込データ）の
 * 同期処理がメモリ・ALB idle timeout を超えないための安全弁。サーバ側 PaymentJobService.MAX_EXPORT_HOUSEHOLDS と整合させること。
 */
const MAX_SELECTABLE_HOUSEHOLDS = 1000

const statusOptions = [
  { value: '', label: 'ステータス（全て）' },
  { value: 'AWAITING_CALC', label: '給付額算出自動実行待' },
  { value: 'CALC_IN_PROGRESS', label: '給付額算出中' },
  { value: '__HAS_CALC_ERROR__', label: '給付額算出エラー' },
  /** バックエンド {@link PaymentJobStatus.CALC_DONE} と一致（CALCULATED は無効で 400） */
  { value: 'CALC_DONE', label: '給付額算出済' },
  { value: 'NOTICE_PRINTED', label: '給付決定通知書作成済' },
  { value: 'NOTICE_DOWNLOADED', label: '給付決定通知書出力済' },
  { value: 'ZENGIN_PROD_EXPORTED', label: '完了（振込データ出力済）' },
  { value: 'COMPLETED_CASH', label: '完了（現金手渡し済）' },
  { value: '__ALL_DENIED__', label: '完了（不支給）' },
  { value: '__ALL_PLAINTIFF_CALC__', label: '完了（原告のため給付額算出まで）' },
]

// ステータス（全て）
const DEFAULT_STATUS = ''

const DEFAULT_SEARCH: SearchValues = {
  jobNo: '',
  jobName: '',
  status: DEFAULT_STATUS,
  registeredBy: '',
  includeDeleted: false,
  transferDateFrom: null,
  transferDateTo: null,
  excludeCompleted: false,
}
const CALCULATING_LABEL = '給付額算出中'
const CALC_ERROR_LABEL = '給付額算出エラー'

// 編集／削除を block する PaymentJob status。2026-05-14 仕様で NOTICE_PRINTED は
// 編集可となったため除外し、NOTICE_DOWNLOADED 以降のみ lock 対象とする。
const LOCKED_STATUS_CODES = new Set([
  'NOTICE_DOWNLOADED',
  'ZENGIN_PROD_EXPORTED',
  'COMPLETED_CASH',
])

// 完了系（不支給／原告終端／現金手渡し）は派生ラベルで表示されるためフラグでも判定する。
// 親 statusCode が CALC_DONE のままでも、全 line が完了状態なら編集／削除は不可。
function isJobLocked(job: PaymentJobResponse): boolean {
  if (LOCKED_STATUS_CODES.has(job.statusCode)) return true
  if (job.allDeniedCompleted) return true
  if (job.allPlaintiffCalcCompleted) return true
  return false
}

function normalizeSearchValues(values: Partial<SearchValues>): SearchValues {
  let status = values.status ?? ''
  if (status === 'CALCULATED') status = 'CALC_DONE'
  return {
    jobNo: values.jobNo?.trim() ?? '',
    jobName: values.jobName?.trim() ?? '',
    status,
    registeredBy: values.registeredBy?.trim() ?? '',
    includeDeleted: values.includeDeleted ?? false,
    transferDateFrom: values.transferDateFrom ?? null,
    transferDateTo: values.transferDateTo ?? null,
    excludeCompleted: values.excludeCompleted ?? false,
  }
}

function resolveFilename(response: AxiosResponse<Blob>, fallback: string): string {
  const contentDisposition = response.headers['content-disposition']
  if (typeof contentDisposition !== 'string') {
    return fallback
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1])
    } catch {
      return utf8Match[1]
    }
  }

  const simpleMatch = contentDisposition.match(/filename="?([^"]+)"?/i)
  return simpleMatch?.[1] ?? fallback
}

function getStatusDisplayLabel(job: PaymentJobResponse): string {
  // 全 line が原告終端（COMPLETED_PLAINTIFF_CALC）の場合、ジョブ status は CALC_DONE 据え置き
  // だが、画面上は原告終端ラベルを表示する。
  if (job.allPlaintiffCalcCompleted) {
    return '完了（原告のため給付額算出まで）'
  }
  // 全 line が不支給終端（COMPLETED_DENIED）の場合も同様。
  if (job.allDeniedCompleted) {
    return '完了（不支給）'
  }
  // line に 1 件でもエラーがあればジョブ全体を「給付額算出エラー」として表示する。
  // 親 status が CALC_ERROR=全員エラーでなくても、一部エラーをエラー表示へ集約する
  // （「一部エラー」混在ラベルは廃止。エラーがあれば計算エラー扱い）。
  if (job.hasCalcError) {
    return CALC_ERROR_LABEL
  }
  // 計算進行中の混在: 完了 line と未完了/進行中 line が共存。親が既に CALC_DONE なら混在ではない。
  if (
    job.hasCalcDone &&
    (job.hasAwaitingCalc || job.hasCalcInProgress) &&
    job.statusCode !== 'CALC_DONE'
  ) {
    return CALCULATING_LABEL
  }
  // 通常状態: backend の派生ラベル（CaseStatusService の sync フックで受給者状態と一致）
  return job.statusLabel
}

const PaymentJobList = () => {
  const goBackToParent = useBackToParent()
  const navigateTo = useAppNavigate()
  const { hasPermission, isAdmin } = usePermission()
  const toast = useToast()
  const selectAllRef = useRef<HTMLInputElement>(null)

  // 振込予定日の既定値（今日〜1ヶ月後）。session に保存が無い初回アクセスのみ既定値として用い、
  // 以降はユーザが最後に適用した日付（クリアで空にした場合は空）を復元する。
  const defaultSearch = useMemo<SearchValues>(() => ({
    ...DEFAULT_SEARCH,
    transferDateFrom: dayjs().format('YYYY-MM-DD'),
    transferDateTo: dayjs().add(1, 'month').format('YYYY-MM-DD'),
  }), [])
  const [savedSearch, setSavedSearch] = useSearchPersistence<SearchValues>('payment-job-list', defaultSearch)
  // page / sort は SearchValues に含めず別キーで最小限に永続化する（既存の filter 永続化はそのまま）。
  const [savedPage, setSavedPage] = useSearchPersistence<number>('payment-job-list:page', 1)
  const [savedSort, setSavedSort] = useSearchPersistence<SortState>('payment-job-list:sort', { sortBy: null, order: null })
  const [searchParams, setSearchParams] = useSearchParams()

  // URL preset (deep-link): URL params override session-saved state on first render.
  // Parsed once at mount; consumed and stripped so a manual clear/search later behaves normally.
  const urlPreset = useMemo<Partial<SearchValues> | null>(() => {
    const transferDateToParam = searchParams.get('transferDateTo')
    const excludeStatusesParam = searchParams.get('excludeStatuses')
    if (!transferDateToParam && !excludeStatusesParam) return null
    const preset: Partial<SearchValues> = {}
    if (transferDateToParam) {
      preset.transferDateTo = transferDateToParam
    }
    if (excludeStatusesParam) {
      const tokens = excludeStatusesParam.split(',').map((token) => token.trim())
      if (tokens.includes('ZENGIN_PROD_EXPORTED') || tokens.includes('COMPLETED_CASH')) {
        preset.excludeCompleted = true
      }
    }
    return preset
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 振込予定日の初期値：
  //  - URL preset (announcement 由来) があれば preset の transferDateTo のみを採用し、From は未設定。
  //    過去日の未完了ジョブも含めて transferDateTo 以前を全件表示するため、savedSearch はマージしない。
  //    preset は一回限り（消費時に session を既定値へ戻す）ため、次回の直接アクセスでは残らない。
  //  - URL preset が無い「直接アクセス」では、前回適用した検索条件（振込予定日含む）を復元する。
  //    初回（session 未保存）のみ defaultSearch の「今日 〜 1ヶ月後」を既定値として使う。
  const initialSearch = useMemo<SearchValues>(() => {
    if (urlPreset) {
      return normalizeSearchValues({ ...DEFAULT_SEARCH, ...urlPreset })
    }
    return normalizeSearchValues(savedSearch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [qJobNo, setQJobNo] = useState(initialSearch.jobNo)
  const [qJobName, setQJobName] = useState(initialSearch.jobName)
  const [qStatus, setQStatus] = useState<string>(initialSearch.status)
  const [qRegisteredBy, setQRegisteredBy] = useState(initialSearch.registeredBy)
  const [qIncludeDeleted, setQIncludeDeleted] = useState(initialSearch.includeDeleted)
  const [qTransferDateFrom, setQTransferDateFrom] = useState<string | null>(initialSearch.transferDateFrom)
  const [qTransferDateTo, setQTransferDateTo] = useState<string | null>(initialSearch.transferDateTo)
  const [qExcludeCompleted, setQExcludeCompleted] = useState(initialSearch.excludeCompleted)

  const [appliedSearch, setAppliedSearch] = useState<SearchValues>(initialSearch)

  // URL preset wins: persist into savedSearch and strip query params from URL after consumption,
  // so further interactions (clear/search) follow normal state flow.
  // excludeCompleted は UI トグルが無いため、deep-link 一回限りの作用に留め localStorage には
  // false で保存する。これにより、URL 引数なしで再訪問したときに「気付かないうちに完了系除外」が
  // 残ったままになることを防ぐ。
  useEffect(() => {
    if (!urlPreset) return
    // preset（announcement 由来）は一回限り。次回の直接アクセスで preset の日付・除外条件が
    // 残らないよう、session には既定値（今日〜1ヶ月後）を保存する。
    setSavedSearch({ ...defaultSearch })
    const next = new URLSearchParams(searchParams)
    next.delete('transferDateTo')
    next.delete('excludeStatuses')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingDeleteJob, setPendingDeleteJob] = useState<PaymentJobResponse | null>(null)
  const [bankIssues, setBankIssues] = useState<BankInfoIssue[]>([])
  const [bankIssueDialogOpen, setBankIssueDialogOpen] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState<string | null>(null)

  useEffect(() => {
    if (savedSearch.status !== 'CALCULATED') return
    const next = normalizeSearchValues(savedSearch)
    // 旧ステータス CALCULATED を現行 CALC_DONE へ移行する一度きりの同期。
    /* eslint-disable react-hooks/set-state-in-effect */
    setSavedSearch(next)
    setQStatus(next.status)
    setAppliedSearch((prev) => (prev.status === 'CALCULATED' ? { ...prev, status: 'CALC_DONE' } : prev))
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [savedSearch, setSavedSearch])

  const [rows, setRows] = useState<PaymentJobResponse[]>([])
  const [total, setTotal] = useState(0)
  // URL preset（deep-link）はフィルタをリセットするため page / sort も初期化し、
  // それ以外（通常遷移）は前回適用したページ・並び順を復元する。
  const [page, setPage] = useState(urlPreset ? 1 : (savedPage ?? 1))
  const [pageSize, setPageSize] = usePersistentPageSize('payment-job-list')
  const [loading, setLoading] = useState(true)
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([])
  const [selectedJobMap, setSelectedJobMap] = useState<Map<number, PaymentJobResponse>>(new Map())
  const { sort, setSort, sortQuery } = useSortState(urlPreset ? { sortBy: null, order: null } : savedSort)
  const { widths: colWidths, startResize } = useResizableColumns({ defaults: PAYMENT_JOB_LIST_COLUMN_DEFAULTS })

  // 列表示設定（No.30 横展開）。表示中の列だけに絞った順序配列を導出し、colgroup/thead/tbody/幅計算を統一する。
  const { isVisible: isColVisible, setVisible: setColVisible, reset: resetCols, hiddenCount: hiddenColCount } =
    useColumnVisibility(PAYMENT_JOB_LIST_COLUMN_VISIBILITY_KEY, PAYMENT_JOB_LIST_COLUMN_ORDER, {
      alwaysVisible: PAYMENT_JOB_LIST_ALWAYS_VISIBLE_COLUMNS,
    })
  const visibleColumnOrder = PAYMENT_JOB_LIST_COLUMN_ORDER.filter((id) => isColVisible(id))
  // 隠した列は colWidths から除外して合計幅を出す（隙間/幅ズレ防止）。
  const tableWidth = visibleColumnOrder.reduce((sum, id) => sum + (colWidths[id] ?? 0), 0)
  // tbody の空行/ローディング行 colSpan は「表示中の列数 + 末尾の余白 col(1)」。
  const visibleColSpan = visibleColumnOrder.length + 1
  const handleSortChange = (next: typeof sort) => {
    setSort(next)
    setPage(1)
  }

  const { totalPages, safePage, rangeFrom, rangeTo } = computePagination(page, total, pageSize)
  const selectedIdSet = useMemo(() => new Set(selectedRowIds), [selectedRowIds])

  const loadJobs = useCallback(async () => {
    setLoading(true)
    try {
      const isCalcError = appliedSearch.status === '__HAS_CALC_ERROR__'
      const isCalculated = appliedSearch.status === 'CALC_DONE'
      const isAllPlaintiff = appliedSearch.status === '__ALL_PLAINTIFF_CALC__'
      const isAllDenied = appliedSearch.status === '__ALL_DENIED__'
      const isClientSideFilter = isCalculated || isAllPlaintiff || isAllDenied

      let apiStatus: string | undefined
      if (isCalcError) {
        apiStatus = 'CALC_ERROR'
      } else if (isAllPlaintiff || isAllDenied) {
        apiStatus = undefined
      } else {
        apiStatus = appliedSearch.status || undefined
      }

      const { data } = await paymentJobsApi.list({
        jobNo: appliedSearch.jobNo || undefined,
        jobName: appliedSearch.jobName || undefined,
        registeredBy: appliedSearch.registeredBy || undefined,
        status: apiStatus,
        excludeDeleted: appliedSearch.includeDeleted ? undefined : true,
        transferDateFrom: appliedSearch.transferDateFrom || undefined,
        transferDateTo: appliedSearch.transferDateTo || undefined,
        excludeStatuses: appliedSearch.excludeCompleted ? EXCLUDE_COMPLETED_STATUSES : undefined,
        ...sortQuery,
        page: isClientSideFilter ? 0 : safePage - 1,
        size: isClientSideFilter ? 9999 : pageSize,
      })
      let filteredContent = data.content
      if (isCalculated) {
        filteredContent = filteredContent.filter(
          (job) => !job.hasCalcError && !job.allPlaintiffCalcCompleted && !job.allDeniedCompleted,
        )
      } else if (isAllPlaintiff) {
        filteredContent = filteredContent.filter((job) => job.allPlaintiffCalcCompleted)
      } else if (isAllDenied) {
        filteredContent = filteredContent.filter((job) => job.allDeniedCompleted)
      }
      setRows(filteredContent)
      setTotal(isClientSideFilter ? filteredContent.length : data.totalElements)
    } catch (error) {
      setRows([])
      setTotal(0)
      if (isHandledAuthError(error)) return
    } finally {
      setLoading(false)
    }
  }, [appliedSearch, safePage, pageSize, sortQuery])

  useEffect(() => {
    // 検索条件・ページ変更に応じて一覧を取得する正当なデータ取得 effect。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadJobs()
  }, [loadJobs])

  useEffect(() => {
    // 総件数減少で範囲外になったページを有効ページへ寄せる補正。
    if (page !== safePage) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPage(safePage)
    }
  }, [page, safePage])

  // 現在のページ・並び順を別キーで永続化し、再訪問時に復元する。
  useEffect(() => {
    setSavedPage(page)
    setSavedSort(sort)
  }, [page, sort, setSavedPage, setSavedSort])

  useEffect(() => {
    if (rows.length === 0 || selectedRowIds.length === 0) {
      return
    }

    // 取得結果に合わせて選択中ジョブの表示情報を同期する。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedJobMap((prev) => {
      const next = new Map(prev)
      rows.forEach((row) => {
        if (selectedIdSet.has(row.id)) {
          next.set(row.id, row)
        }
      })
      return next
    })
  }, [rows, selectedIdSet, selectedRowIds.length])

  const selectedJobs = useMemo(
    () =>
      selectedRowIds
        .map((id) => selectedJobMap.get(id))
        .filter((row): row is PaymentJobResponse => Boolean(row)),
    [selectedJobMap, selectedRowIds],
  )

  // 選択中リストの対象世帯数の合計（上限判定に使う）。
  const selectedHouseholds = useMemo(
    () => selectedJobs.reduce((sum, job) => sum + (job.recipientCount ?? 0), 0),
    [selectedJobs],
  )

  const selectableRowsOnPage = useMemo(() => rows.filter((row) => !row.deleted), [rows])

  const allPageSelected =
    selectableRowsOnPage.length > 0 && selectableRowsOnPage.every((row) => selectedIdSet.has(row.id))
  const somePageSelected = selectableRowsOnPage.some((row) => selectedIdSet.has(row.id))

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = somePageSelected && !allPageSelected
    }
  }, [allPageSelected, somePageSelected])

  const resetSelection = () => {
    setSelectedRowIds([])
    setSelectedJobMap(new Map())
  }

  const toggleSelectAllOnPage = () => {
    if (allPageSelected) {
      // 本ページの選択解除は件数が減るだけなので上限チェック不要。
      const removed = new Set(selectableRowsOnPage.map((row) => row.id))
      setSelectedRowIds((prev) => prev.filter((id) => !removed.has(id)))
      setSelectedJobMap((prev) => {
        const next = new Map(prev)
        selectableRowsOnPage.forEach((row) => next.delete(row.id))
        return next
      })
      return
    }

    // 本ページを全選択。選択世帯数の合計が上限（MAX_SELECTABLE_HOUSEHOLDS）を超える分は追加しない。
    const currentSet = new Set(selectedRowIds)
    const toAdd = selectableRowsOnPage.filter((row) => !currentSet.has(row.id))
    let runningHouseholds = selectedHouseholds
    const added: PaymentJobResponse[] = []
    for (const row of toAdd) {
      const households = row.recipientCount ?? 0
      if (runningHouseholds + households > MAX_SELECTABLE_HOUSEHOLDS) continue
      runningHouseholds += households
      added.push(row)
    }
    if (added.length < toAdd.length) {
      Modal.error({ content: '選択対象が1,000世帯を超えているため、選択できません。' })
    }
    if (added.length === 0) return

    setSelectedRowIds((prev) => [...prev, ...added.map((row) => row.id)])
    setSelectedJobMap((prev) => {
      const next = new Map(prev)
      added.forEach((row) => next.set(row.id, row))
      return next
    })
  }

  const toggleRow = (row: PaymentJobResponse) => {
    if (row.deleted) return
    // 新規選択時のみ上限チェック（解除は常に許可）。選択世帯数の合計で判定する。
    if (
      !selectedIdSet.has(row.id) &&
      selectedHouseholds + (row.recipientCount ?? 0) > MAX_SELECTABLE_HOUSEHOLDS
    ) {
      Modal.error({ content: '選択対象が1,000世帯を超えているため、選択できません。' })
      return
    }
    setSelectedRowIds((prev) => {
      if (prev.includes(row.id)) {
        return prev.filter((id) => id !== row.id)
      }
      return [...prev, row.id]
    })

    setSelectedJobMap((prev) => {
      const next = new Map(prev)
      if (next.has(row.id)) {
        next.delete(row.id)
      } else {
        next.set(row.id, row)
      }
      return next
    })
  }

  const handleSearch = () => {
    setLoading(true)
    resetSelection()
    setPage(1)
    const searchValues = normalizeSearchValues({
      jobNo: qJobNo,
      jobName: qJobName,
      status: qStatus,
      registeredBy: qRegisteredBy,
      includeDeleted: qIncludeDeleted,
      transferDateFrom: qTransferDateFrom,
      transferDateTo: qTransferDateTo,
      excludeCompleted: qExcludeCompleted,
    })
    setAppliedSearch(searchValues)
    setSavedSearch(searchValues)
  }

  const handleClear = () => {
    setLoading(true)
    setQJobNo('')
    setQJobName('')
    setQStatus(DEFAULT_STATUS)
    setQRegisteredBy('')
    setQIncludeDeleted(false)
    setQTransferDateFrom(null)
    setQTransferDateTo(null)
    setQExcludeCompleted(false)
    resetSelection()
    setPage(1)
    // 2 回目以降のクリアでも loadJobs を必ず再発火させるため、毎回新しいオブジェクト参照を渡す。
    // DEFAULT_SEARCH の参照をそのまま渡すと React が Object.is で bail-out し、setLoading(true)
    // だけが残ってローディング表示が解除されない。
    setAppliedSearch({ ...DEFAULT_SEARCH })
    setSavedSearch({ ...DEFAULT_SEARCH })
  }

  const handleOpenCreate = () => {
    navigateTo('payment-job-create')
  }

  const lockedReason = (job: PaymentJobResponse): string => {
    if (job.allDeniedCompleted) return '完了（不支給）のリスト'
    if (job.allPlaintiffCalcCompleted) return '完了（原告のため給付額算出まで）のリスト'
    if (job.statusCode === 'COMPLETED_CASH') return '完了（現金手渡し済）のリスト'
    return '通知書出力済以降のリスト'
  }

  const handleEditRow = (job: PaymentJobResponse) => {
    if (isJobLocked(job)) {
      toast.warning(`${lockedReason(job)}は編集できません。`)
      return
    }
    navigateTo('payment-job-create', { paymentJobId: job.id })
  }

  const handleDeleteRow = (job: PaymentJobResponse) => {
    // 管理者（ADMIN / SYSTEM_ADMIN）はステータスに関わらず削除可（編集は従来通りステータス制限を維持）。
    if (!isAdmin && isJobLocked(job)) {
      toast.warning(`${lockedReason(job)}は削除できません。`)
      return
    }
    setPendingDeleteJob(job)
    setConfirmOpen(true)
  }

  const executeDelete = async () => {
    if (!pendingDeleteJob) return
    setConfirmOpen(false)
    try {
      await paymentJobsApi.delete(pendingDeleteJob.id)
      toast.success('リストを削除済みとしてマークしました。')
      setSelectedRowIds((prev) => prev.filter((id) => id !== pendingDeleteJob.id))
      setSelectedJobMap((prev) => {
        const next = new Map(prev)
        next.delete(pendingDeleteJob.id)
        return next
      })
      void loadJobs()
    } catch (error) {
      if (isHandledAuthError(error)) return
    } finally {
      setPendingDeleteJob(null)
    }
  }

  const handleNotificationOutput = async () => {
    if (selectedRowIds.length === 0) {
      toast.warning('通知書出力するリストを1件以上選択してください。')
      return
    }
    if (selectedJobs.some((job) => job.statusCode !== 'NOTICE_PRINTED')) {
      toast.warning('給付決定通知書作成済のリストのみ出力できます。')
      return
    }
    setDownloadLoading('給付決定通知書を出力しています...')
    try {
      const response = await paymentJobsApi.printNotices(selectedRowIds)
      const transitioned = Number(response.headers['x-transitioned'] ?? 0)
      const pdfCount = Number(response.headers['x-pdf-count'] ?? 0)
      const missingCount = Number(response.headers['x-missing-count'] ?? 0)

      if (pdfCount > 0) {
        downloadBlob(
          response.data,
          resolveFilename(response, `決定通知書_${new Date().toISOString().slice(0, 10)}.zip`),
        )
      }

      const parts: string[] = []
      parts.push(pdfCount > 0 ? `PDF: ${pdfCount}件` : '出力対象の PDF はありませんでした')
      if (transitioned > 0) parts.push(`ステータス更新: ${transitioned}件`)
      if (missingCount > 0) parts.push(`未生成: ${missingCount}件`)
      const message = `通知書を出力しました。（${parts.join(' / ')}）`
      if (missingCount > 0 || pdfCount === 0) {
        toast.warning(message)
      } else {
        toast.success(message)
      }
      resetSelection()
      void loadJobs()
    } catch (error) {
      if (isHandledAuthError(error)) return
    } finally {
      setDownloadLoading(null)
    }
  }


  const handlePayeesExport = async () => {
    if (selectedRowIds.length === 0) {
      toast.warning('支給者を出力するリストを1件以上選択してください。')
      return
    }

    setDownloadLoading('支給者一覧を出力しています...')
    try {
      // バックエンドで選択リストごとの支給者 CSV（支給者_<jobNo>.csv）を 1 つの ZIP に束ねて返すため、
      // ダウンロードは 1 回（1 ファイル）で完結する。
      const response = await paymentJobsApi.exportPayeesBulk(selectedRowIds)
      downloadBlob(
        response.data,
        resolveFilename(response, `支給者一覧_${new Date().toISOString().slice(0, 10)}.zip`),
      )
      toast.success('支給者一覧を出力しました。')
      resetSelection()
      void loadJobs()
    } catch (error) {
      if (isHandledAuthError(error)) return
      // バックエンドの 400 等のメッセージを blob 本文から取り出して 1 つだけ表示する。
      const message = await extractBlobErrorMessage(error)
      Modal.error({ title: 'エラー', content: message ?? '支給者一覧の出力に失敗しました' })
    } finally {
      setDownloadLoading(null)
    }
  }

  const handleTransferExport = async () => {
    if (selectedRowIds.length === 0) {
      toast.warning('振込データを出力するリストを1件以上選択してください。')
      return
    }
    if (selectedJobs.some((job) => job.statusCode === 'AWAITING_CALC')) {
      toast.warning('給付額算出自動実行待のリストは振込データを出力できません。')
      return
    }

    setDownloadLoading('振込データを出力しています...')
    try {
      // 受給者口座情報（FY2603〜FY2607）の事前チェック。
      // 1 件でも不備があれば DAT 出力を中止し、詳細モーダルを表示する。
      try {
        const { data: validation } = await paymentJobsApi.validateBankInfo({
          jobIds: selectedRowIds,
        })
        if (validation.issues.length > 0) {
          setBankIssues(validation.issues)
          setBankIssueDialogOpen(true)
          return
        }
      } catch (error) {
        if (isHandledAuthError(error)) return
        Modal.error({ content: '受給者口座情報の事前チェックに失敗したため、振込データ出力を中止しました。' })
        return
      }

      // ② 振込データ出力 = 2 ファイルを順にダウンロードする：
      //   (1) 振込データ_*.dat（/export-transfer。状態を ZENGIN_PROD_EXPORTED へ前進させる本体）
      //   (2) 支給者一覧_*.zip（①と同一の /export-payees。リストごとの 支給者_<jobNo>.csv を束ねた ZIP）
      const datResponse = await paymentJobsApi.exportTransfer({
        jobIds: selectedRowIds,
      })
      downloadBlob(
        datResponse.data,
        resolveFilename(datResponse, `振込データ_${new Date().toISOString().slice(0, 10)}.dat`),
      )

      // 支給者一覧 ZIP。dat は既に出力・状態前進済みのため、これが失敗しても dat は手元にあり
      //（支給者一覧は①ボタンでいつでも再取得可）、警告に留めて処理は継続する。
      try {
        const payeeResponse = await paymentJobsApi.exportPayeesBulk(selectedRowIds)
        downloadBlob(
          payeeResponse.data,
          resolveFilename(payeeResponse, `支給者一覧_${new Date().toISOString().slice(0, 10)}.zip`),
        )
        toast.success('振込データと支給者一覧を出力しました。')
      } catch (error) {
        if (isHandledAuthError(error)) return
        const message = await extractBlobErrorMessage(error)
        toast.warning(message ?? '振込データは出力しましたが、支給者一覧の出力に失敗しました。')
      }
      resetSelection()
      void loadJobs()
    } catch (error) {
      if (isHandledAuthError(error)) return
      // 「振込データはすでに出力済み…」等のバックエンドメッセージを blob 本文から取り出して 1 つだけ表示する。
      const message = await extractBlobErrorMessage(error)
      Modal.error({ title: 'エラー', content: message ?? '振込データ出力中に予期しないエラーが発生しました' })
    } finally {
      setDownloadLoading(null)
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {downloadLoading && <Spin fullscreen tip={downloadLoading} />}
      <SystemHeader breadcrumbs={[{ label: '支給者管理' }]} />
      <main className="mx-auto w-full max-w-[1600px] px-6 py-8">
        <BackButton onClick={goBackToParent} />

        <SearchCard className="mb-6">
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <DemoInput
              type="text"
              value={qJobNo}
              onChange={(event) => setQJobNo(event.target.value)}
              placeholder="リスト番号"
            />
            <DemoInput
              type="text"
              value={qJobName}
              onChange={(event) => setQJobName(event.target.value)}
              placeholder="リスト名"
            />
            <DatePicker
              style={{ width: '100%' }}
              size="large"
              className="!rounded-lg !border-gray-300"
              format="YYYY-MM-DD"
              placeholder="振込予定日 From"
              value={qTransferDateFrom ? dayjs(qTransferDateFrom) : null}
              onChange={(value) => setQTransferDateFrom(value ? value.format('YYYY-MM-DD') : null)}
            />
            <DatePicker
              style={{ width: '100%' }}
              size="large"
              className="!rounded-lg !border-gray-300"
              format="YYYY-MM-DD"
              placeholder="振込予定日 To"
              value={qTransferDateTo ? dayjs(qTransferDateTo) : null}
              onChange={(value) => setQTransferDateTo(value ? value.format('YYYY-MM-DD') : null)}
            />
            <DemoSelect value={qStatus} onChange={(event) => setQStatus(event.target.value)}>
              {statusOptions.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </DemoSelect>
            <DemoInput
              type="text"
              value={qRegisteredBy}
              onChange={(event) => setQRegisteredBy(event.target.value)}
              placeholder="登録者"
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
                <input
                  type="checkbox"
                  checked={qIncludeDeleted}
                  onChange={(e) => setQIncludeDeleted(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                削除済みを含む
              </label>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={handleClear} className={demoButtonClass.secondary}>
                クリア
              </button>
              <button type="button" onClick={handleSearch} className={demoButtonClass.primary}>
                検索
              </button>
            </div>
          </div>
        </SearchCard>

        <div className="mb-4 flex flex-nowrap items-center justify-between gap-3 overflow-x-auto">
          <div className="flex flex-nowrap items-center gap-3">
            {hasPermission('PAYMENT_LIST_CREATE') && (
              <button type="button" onClick={handleOpenCreate} className={demoButtonClass.primarySm}>
                給付額算出対象作成
              </button>
            )}
          </div>
          <div className="flex flex-nowrap items-center gap-3">
            {hasPermission('PAYMENT_NOTICE_DOWNLOAD') && (
              <button type="button" onClick={() => void handleNotificationOutput()} className={demoButtonClass.sky}>
                給付決定通知書出力
              </button>
            )}
            {hasPermission('PAYMENT_EXPORT_ZENGIN') && (
              <button
                type="button"
                onClick={() => void handleTransferExport()}
                className={demoButtonClass.violet}
              >
                振込データ出力
              </button>
            )}
            {hasPermission('PAYMENT_EXPORT_PAYEE_LIST') && (
              <button type="button" onClick={() => void handlePayeesExport()} className={demoButtonClass.success}>
                支給者出力
              </button>
            )}
          </div>
        </div>

        <ResultCard
          title={
            // 「列表示設定」はタイトル見出しの直後に近接配置する（上部ツールバーの出力ボタン群とは分ける）。
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-800">作成結果一覧</h2>
              <ColumnVisibilityControl
                columns={PAYMENT_JOB_LIST_COLUMN_VISIBILITY_ITEMS}
                isVisible={isColVisible}
                setVisible={setColVisible}
                onReset={resetCols}
                alwaysVisible={PAYMENT_JOB_LIST_ALWAYS_VISIBLE_COLUMNS}
                hiddenCount={hiddenColCount}
              />
            </div>
          }
        >
          <div className="overflow-x-auto">
            <table className="min-w-full table-fixed" style={{ width: `${tableWidth}px` }}>
              <colgroup>
                {visibleColumnOrder.map((id) => (
                  <col key={id} style={{ width: `${colWidths[id]}px` }} />
                ))}
                <col />
              </colgroup>
              <thead className="border-b-2 border-gray-200 bg-gray-50">
                <tr>
                  {isColVisible('select') && (
                    <th className="border-r border-gray-100 px-2 py-3 text-center text-sm font-medium text-gray-700">
                      <input
                        ref={selectAllRef}
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={toggleSelectAllOnPage}
                        disabled={selectableRowsOnPage.length === 0}
                        className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                        title="このページの行をすべて選択・解除します。"
                      />
                    </th>
                  )}
                  {isColVisible('jobNo') && (
                    <SortableHeader field="jobNo" label="リスト番号" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="jobNo" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('jobName') && (
                    <SortableHeader field="jobName" label="リスト名" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="jobName" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('transferDate') && (
                    <SortableHeader field="transferDate" label="振込指定日" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="transferDate" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('recipientCount') && (
                    <SortableHeader field="recipientCount" label="対象世帯数" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="recipientCount" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('status') && (
                    <SortableHeader field="status" label="ステータス" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="status" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('registrant') && (
                    <SortableHeader field="createdBy" label="作成者" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="registrant" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('registeredAt') && (
                    <SortableHeader field="createdAt" label="作成時間" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="registeredAt" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('updater') && (
                    <SortableHeader field="updatedBy" label="更新者" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="updater" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('updatedAtStr') && (
                    <SortableHeader field="updatedAt" label="更新時間" sort={sort} onSortChange={handleSortChange} className="px-2 py-3 text-sm font-medium text-gray-700 normal-case tracking-normal" trailing={<ResizeHandle columnId="updatedAtStr" onMouseDown={startResize} />} />
                  )}
                  {isColVisible('action') && (
                    <th className="px-2 py-3 text-left text-sm font-medium text-gray-700">操作</th>
                  )}
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody className={`divide-y divide-gray-200 transition-opacity ${loading ? 'opacity-60' : ''}`}>
                {rows.length === 0 ? (
                  loading ? (
                    <TableLoadingRow colSpan={visibleColSpan} />
                  ) : (
                    <tr>
                      <td colSpan={visibleColSpan} className="px-4 py-8 text-center text-sm text-gray-500">
                        該当するリストがありません
                      </td>
                    </tr>
                  )
                ) : (
                  rows.map((job) => {
                    const locked = isJobLocked(job)

                    return (
                      <tr key={job.id} className={job.deleted ? 'bg-gray-200 opacity-60' : `transition hover:bg-blue-50 ${locked ? 'bg-gray-50/60' : ''}`}>
                        {isColVisible('select') && (
                          <td className="border-r border-gray-100 px-2 py-3 text-center align-middle">
                            <input
                              type="checkbox"
                              checked={selectedIdSet.has(job.id)}
                              onChange={() => toggleRow(job)}
                              disabled={job.deleted}
                              className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              aria-label={`${job.jobNo} を選択`}
                            />
                          </td>
                        )}
                        {isColVisible('jobNo') && (
                          <td className="px-2 py-3 text-sm font-mono font-medium">
                            <Tooltip title={job.jobNo} placement="topLeft">
                              <span className="block truncate">{job.jobNo}</span>
                            </Tooltip>
                          </td>
                        )}
                        {isColVisible('jobName') && (
                          <td className="px-2 py-3 text-sm">
                            <Tooltip title={job.jobName} placement="topLeft">
                              <span className="block truncate">{job.jobName}</span>
                            </Tooltip>
                          </td>
                        )}
                        {isColVisible('transferDate') && (
                          <td className="px-2 py-3 text-sm">
                            <Tooltip title={job.transferDate} placement="topLeft">
                              <span className="block truncate">{job.transferDate}</span>
                            </Tooltip>
                          </td>
                        )}
                        {isColVisible('recipientCount') && (
                          <td className="px-2 py-3 text-sm truncate">{job.recipientCount}件</td>
                        )}
                        {isColVisible('status') && (
                          <td className="px-2 py-3">
                            <DemoBadge status={getStatusDisplayLabel(job)} />
                          </td>
                        )}
                        {isColVisible('registrant') && (
                          <td className="px-2 py-3 text-sm">
                            <Tooltip title={job.registeredBy ?? EMPTY_DISPLAY} placement="topLeft">
                              <span className="block truncate">{job.registeredBy ?? EMPTY_DISPLAY}</span>
                            </Tooltip>
                          </td>
                        )}
                        {isColVisible('registeredAt') && (
                          <td className="px-2 py-3 text-sm">
                            <Tooltip title={formatTimestamp(job.createdAt)} placement="topLeft">
                              <span className="block truncate">{formatTimestamp(job.createdAt)}</span>
                            </Tooltip>
                          </td>
                        )}
                        {isColVisible('updater') && (
                          <td className="px-2 py-3 text-sm">
                            <Tooltip title={job.updatedBy ?? EMPTY_DISPLAY} placement="topLeft">
                              <span className="block truncate">{job.updatedBy ?? EMPTY_DISPLAY}</span>
                            </Tooltip>
                          </td>
                        )}
                        {isColVisible('updatedAtStr') && (
                          <td className="px-2 py-3 text-sm">
                            <Tooltip title={formatTimestamp(job.updatedAt)} placement="topLeft">
                              <span className="block truncate">{formatTimestamp(job.updatedAt)}</span>
                            </Tooltip>
                          </td>
                        )}
                        {isColVisible('action') && (
                        <td className="whitespace-nowrap px-2 py-3 text-left">
                          {job.deleted ? (
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={() => navigateTo('payment-job-detail', { paymentJobId: job.id })}
                                className={demoButtonClass.linkBlue}
                              >
                                支給者詳細
                              </button>
                              <span className="text-xs text-gray-400">削除済み</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={() => navigateTo('payment-job-detail', { paymentJobId: job.id })}
                                className={demoButtonClass.linkBlue}
                              >
                                支給者詳細
                              </button>
                              {hasPermission('PAYMENT_LIST_EDIT') && (
                                <button
                                  type="button"
                                  onClick={() => handleEditRow(job)}
                                  className={demoButtonClass.linkBlue}
                                >
                                  編集
                                </button>
                              )}
                              {hasPermission('PAYMENT_LIST_DELETE') && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteRow(job)}
                                  className={demoButtonClass.linkRed}
                                >
                                  削除
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        )}
                        <td aria-hidden="true" />
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={safePage}
            totalPages={totalPages}
            total={total}
            rangeFrom={rangeFrom}
            rangeTo={rangeTo}
            onPageChange={(next) => {
              setLoading(true)
              setPage(next)
            }}
            pageSize={pageSize}
            onPageSizeChange={(next) => {
              setLoading(true)
              setPageSize(next)
              setPage(1)
            }}
          />
        </ResultCard>
      </main>
      <ConfirmDialog
        open={confirmOpen}
        message={`「${pendingDeleteJob?.jobName ?? ''}」を削除済みとしてマークします。\n削除済みのリストは一覧にグレー表示されます。\nよろしいですか？`}
        variant="danger"
        onConfirm={() => void executeDelete()}
        onCancel={() => { setConfirmOpen(false); setPendingDeleteJob(null) }}
      />
      <BankInfoIssueDialog
        open={bankIssueDialogOpen}
        title="受給者口座情報に不備があります"
        description={
          '選択したリストの受給者口座情報に不備があるため、振込データ出力を中止しました。\n下記の不備を解消してから、もう一度「振込データ出力」してください。'
        }
        issues={bankIssues}
        onClose={() => setBankIssueDialogOpen(false)}
      />
    </div>
  )
}

const EMPTY_DISPLAY = '-'

function formatTimestamp(ts: string | undefined | null): string {
  if (!ts) return EMPTY_DISPLAY
  const d = new Date(ts)
  if (isNaN(d.getTime())) return EMPTY_DISPLAY
  const yyyy = d.getFullYear()
  const MM = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const HH = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  const ss = String(d.getSeconds()).padStart(2, '0')
  return `${yyyy}-${MM}-${dd} ${HH}:${mm}:${ss}`
}

export default PaymentJobList
